// Pricing page content module. Holds bilingual copy for `pages/public/Pricing.jsx`
// as `{en: {…}, sv: {…}}` structured data. The page reads
// `PRICING_CONTENT[lang]` and renders every string from this module.
//
// SSOT contract: this file is the ONE place Swedish pricing copy lives.
// Anywhere else and the `check_no_swedish.js` CI scanner fails the build.

export const PRICING_CONTENT = {
  en: {
    seoTitle: "Pricing | Talentsio",
    seoDescription: "Posting a booking request and receiving offers is free. Talentsio's fee is ten percent, added on top of the artist's price and shown before you pay.",
    heroEyebrow: "Pricing",
    heroTitle: "What does it cost?",
    heroSubtitle: "Posting a request and comparing offers is free. We only charge when a booking happens.",
    faqTitle: "Common questions about pricing",
    toggleBook: "I want to book",
    toggleArtist: "I'm an artist",
    faq: [
      { q: "What does it cost to post a request?",
        a: "Nothing. Posting a request and receiving offers is free, and you are not committing to anything by doing so." },
      { q: "Does Talentsio take commission on the booking?",
        a: "Yes, ten percent plus VAT on the fee. It's added on top of the artist's price and shown before you pay. It's never taken from the artist's fee, so the price the artist quotes is the price the artist receives." },
      { q: "What does it cost for me as an artist?",
        a: "You pay one credit to unlock a request and see the customer's contact details. If it becomes a booking, Talentsio takes ten percent. No monthly fee and no lock-in." },
      { q: "How many can respond to the same request?",
        a: "At most five. The customer gets a few offers to compare instead of a flooded inbox, and the artist knows the competition is capped before spending time on a reply." },
      { q: "When does the artist get paid?",
        a: "Fourteen calendar days after the customer has paid. The payment goes to the artist's own account and Talentsio never holds the money." },
      { q: "Does it cost anything to cancel?",
        a: "Cancellation terms are in the contract and depend on how close to the event the cancellation happens. If the artist cancels, the customer always gets the full amount back." },
    ],
    // Client-side ("I want to book") copy
    client: {
      big1Label: "To post a request",
      big1Sub: "Describe the event, receive offers, compare. You are not committing to anything.",
      big2Label: "To receive up to {cap} offers",
      big2Sub: "The cap is deliberate. A few offers can be compared, fifty cannot be read.",
      big3Label: "When you book",
      big3Sub: "The fee is added on top of the artist's price, plus {vat} percent VAT on the fee. It's shown before you pay.",
      exampleTitle: "A worked example",
      exampleSub: "The numbers are calculated by the same code the checkout uses. What you see here is what you pay there.",
      artistPrice: "Artist's price",
      platformFee: "Talentsio's fee, {fee} percent",
      vatOnFee: "VAT on the fee",
      youPay: "You pay",
      artistGets: "The artist receives",
      exampleFooter: "The fee is never deducted from the fee to the artist. The price the artist quotes is the price the artist receives, and that's why the offers you compare are actually comparable.",
      neverPayTitle: "Things you never pay for",
      neverPayItems: [
        "Posting a request",
        "Receiving and comparing offers",
        "Chatting with artists before you choose",
        "The contract, which is drafted automatically",
        "Having an account",
      ],
      howPaymentTitle: "How payment works",
      howPaymentBody1: "You pay once you have accepted an offer and the contract is signed. The money goes to the artist's own account, not to us. The artist receives it {days} calendar days after your payment, so both parties know exactly where they stand before the event.",
      howPaymentBody2: "If the artist cancels, you get the full amount back, the fee included.",
      cta: "Post a request",
      ctaSub: "Takes under five minutes. Costs nothing.",
    },
    // Talent-side ("I'm an artist") copy
    talent: {
      big1Value: "{n} credits",
      big1Label: "To get started",
      big1Sub: "Enough for that many unlocks. You have time to see if the platform gets you gigs before paying anything.",
      big2Value: "{n} credit",
      big2Label: "Per unlocked request",
      big2Sub: "Only then do you see the customer's contact details. At most {cap} artists can unlock the same request.",
      big3Label: "When the gig happens",
      big3Sub: "No monthly fee, no lock-in, no fee on anything that doesn't turn into a booking.",
      packsTitle: "Credit packs",
      packsSub: "Same price for talents and agencies. Credits never expire.",
      mostChosen: "Most chosen",
      credit: "credit",
      credits: "credits",
      perCredit: "per credit",
      pricesInclVat: "Prices are inclusive of VAT.",
      neverPayTitle: "What you never pay for",
      neverPayItems: [
        "Monthly fee",
        "Lock-in",
        "Having a profile",
        "Requests you choose not to reply to",
        "Cancellation",
      ],
      whyCreditsTitle: "Why credits and not free access",
      whyCreditsBody1: "Without a cost, everyone replies to everything, the customer gets fifty offers and reads three. With a cost, those who mean business reply, and the {cap} artist cap per request means your offer actually gets read.",
      whyCreditsBody2: "Commission is only charged when the gig happens. We earn when you do.",
      cta: "Create an artist profile",
      ctaSub: "{n} credits included when your profile is complete.",
    },
  },
  sv: {
    seoTitle: "Priser | Talentsio",
    seoDescription: "Gratis att lägga upp en förfrågan och ta emot offerter. Talentsios avgift är tio procent, den ligger ovanpå artistens pris och syns innan du betalar.",
    heroEyebrow: "Priser",
    heroTitle: "Vad kostar det?",
    heroSubtitle: "Att lägga upp en förfrågan och jämföra offerter är gratis. Vi tar betalt först när en bokning blir av.",
    faqTitle: "Vanliga frågor om priset",
    toggleBook: "Jag ska boka",
    toggleArtist: "Jag är artist",
    faq: [
      { q: "Vad kostar det att lägga upp en förfrågan?",
        a: "Ingenting. Att lägga upp en förfrågan och ta emot offerter är gratis, och du binder dig inte till något genom att göra det." },
      { q: "Tar Talentsio provision på bokningen?",
        a: "Ja, tio procent plus moms på avgiften. Den läggs ovanpå artistens pris och visas innan du betalar. Den dras aldrig från artistens gage, så det pris artisten offererar är det artisten får." },
      { q: "Vad kostar det för mig som artist?",
        a: "Du betalar en kredit för att låsa upp en förfrågan och se kundens kontaktuppgifter. Blir det en bokning tar Talentsio tio procent. Ingen månadsavgift och ingen bindningstid." },
      { q: "Hur många kan svara på samma förfrågan?",
        a: "Högst fem. Kunden får några offerter att jämföra i stället för en full inkorg, och artisten vet att konkurrensen är begränsad innan hen lägger tid på ett svar." },
      { q: "När får artisten sina pengar?",
        a: "Fjorton kalenderdagar efter att kunden betalat. Betalningen går till artistens eget konto och Talentsio håller aldrig pengarna." },
      { q: "Kostar det något att avboka?",
        a: "Avbokningsvillkoren står i avtalet och beror på hur nära eventet avbokningen sker. Ställer artisten in får kunden alltid tillbaka hela beloppet." },
    ],
    client: {
      big1Label: "Att lägga upp en förfrågan",
      big1Sub: "Beskriv eventet, få offerter, jämför. Du binder dig inte till något.",
      big2Label: "Att ta emot upp till {cap} offerter",
      big2Sub: "Taket är medvetet. Några offerter går att jämföra, femtio går inte att läsa.",
      big3Label: "När du bokar",
      big3Sub: "Avgiften läggs ovanpå artistens pris, plus {vat} procent moms på avgiften. Den visas innan du betalar.",
      exampleTitle: "Ett räkneexempel",
      exampleSub: "Talen räknas av samma kod som kassan använder. Det du ser här är det du betalar där.",
      artistPrice: "Artistens pris",
      platformFee: "Talentsios avgift, {fee} procent",
      vatOnFee: "Moms på avgiften",
      youPay: "Du betalar",
      artistGets: "Artisten får",
      exampleFooter: "Avgiften dras aldrig från gaget. Det pris artisten offererar är det artisten får, och det är därför offerterna du jämför är jämförbara.",
      neverPayTitle: "Det här betalar du aldrig för",
      neverPayItems: [
        "Att lägga upp en förfrågan",
        "Att ta emot och jämföra offerter",
        "Att chatta med artisterna innan du väljer",
        "Kontraktet, som skrivs automatiskt",
        "Att ha ett konto",
      ],
      howPaymentTitle: "Så går betalningen",
      howPaymentBody1: "Du betalar när du accepterat en offert och avtalet är signerat. Pengarna går till artistens eget konto, inte till oss. Artisten får dem {days} kalenderdagar efter din betalning, så att båda parter vet exakt vad som gäller innan eventet.",
      howPaymentBody2: "Ställer artisten in får du tillbaka hela beloppet, avgiften inräknad.",
      cta: "Lägg upp en förfrågan",
      ctaSub: "Tar under fem minuter. Kostar ingenting.",
    },
    talent: {
      big1Value: "{n} krediter",
      big1Label: "När du kommer i gång",
      big1Sub: "Räcker till lika många upplåsningar. Du hinner se om plattformen ger dig jobb innan du betalar något.",
      big2Value: "{n} kredit",
      big2Label: "Per upplåst förfrågan",
      big2Sub: "Först då ser du kundens kontaktuppgifter. Högst {cap} artister kan låsa upp samma förfrågan.",
      big3Label: "När jobbet blir av",
      big3Sub: "Ingen månadsavgift, ingen bindningstid, ingen avgift på det som inte blir en bokning.",
      packsTitle: "Kreditpaket",
      packsSub: "Samma pris för talanger och byråer. Krediter förfaller aldrig.",
      mostChosen: "Mest vald",
      credit: "kredit",
      credits: "krediter",
      perCredit: "per kredit",
      pricesInclVat: "Priserna är inklusive moms.",
      neverPayTitle: "Vad du aldrig betalar för",
      neverPayItems: [
        "Månadsavgift",
        "Bindningstid",
        "Att ha en profil",
        "Förfrågningar du väljer att inte svara på",
        "Uppsägning",
      ],
      whyCreditsTitle: "Varför krediter och inte fri tillgång",
      whyCreditsBody1: "Utan en kostnad svarar alla på allt, kunden får femtio offerter och läser tre. Med en kostnad svarar den som menar allvar, och taket på {cap} artister per förfrågan gör att din offert faktiskt läses.",
      whyCreditsBody2: "Provisionen tas först när jobbet blir av. Vi tjänar pengar när du gör det.",
      cta: "Skapa en artistprofil",
      ctaSub: "{n} krediter på köpet när profilen är klar.",
    },
  },
};
